import React, { useState, useEffect } from 'react';
import { pagoTransferencia } from '../../services/pagosServices';
import { obtenerCentro, obtenerCuentasBancarias, obtenerPlanes } from '../../services/centroService';
import { verificarHijos, obtenerMatriculaPorId, actualizarMatricula } from '../../services/matriculaService';
import { obtenerFechasPago, obtenerTodosControlPagos, verificarPagosAnterioresHistorial } from '../../services/pagosServices';
import { ToastContainer, toast } from 'react-toastify';
import { Check, Loader, RefreshCw, ArrowLeft } from 'lucide-react';
import { FaFile, FaTrash, FaExchangeAlt } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const PagoMultipleTransferencia = () => {
    const [comprobante, setComprobante] = useState(null);
    const [amount, setAmount] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [imagenAmpliada, setImagenAmpliada] = useState(false);
    const [cuentasBancarias, setCuentasBancarias] = useState([]);
    const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);
    const [hijos, setHijos] = useState([]);
    const [hijosSeleccionados, setHijosSeleccionados] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [nombreArchivo, setNombreArchivo] = useState('');
    const [archivoError, setArchivoError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [tienePagosAnteriores, setTienePagosAnteriores] = useState(false);
    const [mostrarSeleccionPlan, setMostrarSeleccionPlan] = useState(false);
    const [planes, setPlanes] = useState([]);
    const [planSeleccionado, setPlanSeleccionado] = useState(null);
    const [planActual, setPlanActual] = useState(null);
    const [loadingPlanes, setLoadingPlanes] = useState(false);
    const [hijoEnSeleccion, setHijoEnSeleccion] = useState(null);
    const [vistaSeleccionPlan, setVistaSeleccionPlan] = useState(false);

    // Función para cargar planes para un hijo específico
    const cargarPlanesParaHijo = async (hijo, montoActual = null) => {
        try {
            setLoadingPlanes(true);
            const response = await obtenerPlanes();
            if (response.success) {
                setPlanes(response.data);
                
                // Buscar el plan actual del hijo
                let planActualEncontrado = null;
                
                if (montoActual && montoActual > 0) {
                    // Primero buscar por monto
                    planActualEncontrado = response.data.find(plan => 
                        Number(plan.monto) === montoActual
                    );
                }
                
                // Si no se encontró por monto, buscar por id_plan del hijo
                if (!planActualEncontrado) {
                    planActualEncontrado = response.data.find(plan => 
                        plan.id_plan === hijo.id_plan
                    );
                }
                
                setPlanActual(planActualEncontrado || null);
                setPlanSeleccionado(planActualEncontrado || null);
                
                // NO establecer el monto del plan actual aquí - solo cuando se confirme
                if (planActualEncontrado) {
                    // setAmount(Number(planActualEncontrado.monto) || 0);
                    // Guardar temporalmente el plan seleccionado para la vista superpuesta
                    localStorage.setItem('planSeleccionadoTemp', JSON.stringify(planActualEncontrado));
                }
            }
        } catch (error) {
            console.error('Error al cargar planes:', error);
            showErrorToast('Error al cargar los planes disponibles');
        } finally {
            setLoadingPlanes(false);
        }
    };

    // Función para verificar pagos anteriores
    const verificarPagosAnteriores = async (idMatricula) => {
        try {
            console.log('Verificando pagos anteriores para matrícula:', idMatricula);
            const response = await verificarPagosAnterioresHistorial(idMatricula);
            console.log('Respuesta de verificación de historial:', response);
            
            if (response.success && response.tienePagosAnteriores) {
                console.log('Tiene pagos anteriores en historial - mostrando selección de plan');
                setTienePagosAnteriores(true);
                // Para obtener el monto, necesitamos consultar las fechas de pago
                const fechasResponse = await obtenerFechasPago(idMatricula);
                let montoActual = 0;
                if (fechasResponse.data && fechasResponse.data.length > 0) {
                    const ultimoPago = fechasResponse.data[fechasResponse.data.length - 1];
                    montoActual = ultimoPago.monto ? Number(ultimoPago.monto) : 0;
                }
                return { tienePagos: true, montoActual };
            } else {
                console.log('NO tiene pagos anteriores en historial - primera vez pagando');
                setTienePagosAnteriores(false);
                return { tienePagos: false, montoActual: 0 };
            }
        } catch (error) {
            console.error('Error al verificar pagos anteriores:', error);
            // En caso de error, asumimos que es primera vez
            setTienePagosAnteriores(false);
            return { tienePagos: false, montoActual: 0 };
        }
    };

    useEffect(() => {
        const obtenerDatos = async () => {
            try {
                // Obtener cuentas bancarias
                const cuentasResponse = await obtenerCuentasBancarias();
                if (cuentasResponse.success) {
                    setCuentasBancarias(cuentasResponse.data);
                }

                // Obtener hijos del padre
                const padreId = JSON.parse(localStorage.getItem('user'))?.id;
                if (padreId) {
                    const responseHijos = await verificarHijos(padreId);
                    if (responseHijos.hijos) {
                        // Filtrar solo hijos con matrícula activa
                        const hijosActivos = responseHijos.hijos.filter(hijo => hijo.estado_matricula === 'Activa');
                        const hijosConPago = await Promise.all(
                            hijosActivos.map(async (hijo) => {
                                try {
                                    const fechasResponse = await obtenerFechasPago(hijo.id_matricula);
                                    const fechaActual = new Date();
                                    fechaActual.setHours(0, 0, 0, 0);
                                    const ultimoRegistro = fechasResponse.data && fechasResponse.data.length > 0 
                                        ? fechasResponse.data[fechasResponse.data.length - 1] 
                                        : null;
                                    const proximaFechaPago = ultimoRegistro ? new Date(ultimoRegistro.proxima_fecha_pago) : null;
                                    if (proximaFechaPago) {
                                        proximaFechaPago.setHours(0, 0, 0, 0);
                                    }
                                    
                                    let requierePago = proximaFechaPago ? fechaActual >= proximaFechaPago : true;
                                    let pagoTarjetaPendiente = false;
                                    let transferenciaPendiente = false;
                                    
                                    // Usar la misma lógica que Pago_multiple.js - verificar el último registro
                                    if (ultimoRegistro && ultimoRegistro.estado_verificacion === 'Pendiente') {
                                        // Verificar si es una transferencia pendiente de verificación
                                        transferenciaPendiente = ultimoRegistro.tipo_pago === 'Transferencia';
                                        
                                        // Verificar si es un pago con tarjeta pendiente
                                        pagoTarjetaPendiente = ultimoRegistro.tipo_pago === 'Tarjeta';
                                        
                                        // Si hay transferencia o pago por tarjeta pendiente, no requiere pago
                                        if (transferenciaPendiente || pagoTarjetaPendiente) {
                                            requierePago = false;
                                        }
                                    }
                                    
                                    // Si el último registro fue rechazado, siempre requiere pago
                                    if (ultimoRegistro && ultimoRegistro.estado_verificacion === 'Rechazado') {
                                        requierePago = true;
                                        transferenciaPendiente = false;
                                        pagoTarjetaPendiente = false;
                                    }
                                    
                                    return {
                                        ...hijo,
                                        requierePago,
                                        pagoTarjetaPendiente,
                                        transferenciaPendiente
                                    };
                                } catch (fechaError) {
                                    console.warn(`Error al obtener fechas para matrícula ${hijo.id_matricula}:`, fechaError.message || fechaError);
                                    return {
                                        ...hijo,
                                        requierePago: true
                                    };
                                }
                            })
                        );
                        
                        setHijos(hijosConPago);
                        
                        // Verificar si algún hijo tiene pagos anteriores (comparando fechas de matrícula)
                        let algunHijoTienePagos = false;
                        for (const hijo of hijosActivos) {
                            const fechasResponse = await obtenerFechasPago(hijo.id_matricula);
                            if (fechasResponse.success && fechasResponse.data && fechasResponse.data.length > 0) {
                                // Obtener información de la matrícula para comparar fechas
                                try {
                                    const matriculaInfo = await obtenerMatriculaPorId(hijo.id_child);
                                    if (matriculaInfo && matriculaInfo.matricula && matriculaInfo.matricula.matricula.fecha_matricula) {
                                        const fechaMatricula = new Date(matriculaInfo.matricula.matricula.fecha_matricula).toDateString();
                                        const primerControlPago = fechasResponse.data[0];
                                        const fechaPrimerControl = new Date(primerControlPago.proxima_fecha_pago).toDateString();
                                        
                                        // Si la fecha del primer control de pago NO coincide con la fecha de matrícula
                                        // o hay más de un registro, entonces tiene pagos anteriores
                                        if (fechaMatricula !== fechaPrimerControl || fechasResponse.data.length > 1) {
                                            algunHijoTienePagos = true;
                                            break;
                                        }
                                    } else {
                                        // Si no se puede obtener la fecha de matrícula, asumir que tiene pagos anteriores
                                        algunHijoTienePagos = true;
                                        break;
                                    }
                                } catch (matriculaError) {
                                    console.warn('Error al obtener información de matrícula:', matriculaError);
                                    // En caso de error, asumir que tiene pagos anteriores
                                    algunHijoTienePagos = true;
                                    break;
                                }
                            }
                        }
                        
                        // Si algún hijo tiene pagos anteriores, mostrar selección de plan
                        // Configurar estado basado en si hay hijos con pagos anteriores
                        if (algunHijoTienePagos) {
                            console.log('Al menos un hijo tiene pagos anteriores');
                            setTienePagosAnteriores(true);
                        } else {
                            console.log('Ningún hijo tiene pagos anteriores - primera vez pagando');
                            setTienePagosAnteriores(false);
                        }
                        
                        // No mostrar selección de plan hasta que se seleccione un hijo
                        setMostrarSeleccionPlan(false);
                        
                        // Obtener el precio del plan desde el primer hijo activo
                        
                        if (hijosActivos.length > 0) {
                            const primerHijo = hijosActivos[0];
                            
                            if (primerHijo && primerHijo.plan_monto) {
                                const precio = Number(primerHijo.plan_monto);
                                setAmount(isNaN(precio) ? 0 : precio);
                            } else {
                                console.warn('No se pudo obtener el precio del plan');
                                console.warn('Datos del primer hijo disponibles:', {
                                    plan_monto: primerHijo?.plan_monto,
                                    plan_nombre: primerHijo?.plan_nombre,
                                    plan_tipo: primerHijo?.plan_tipo
                                });
                                setAmount(0);
                            }
                        } else {
                            console.warn('No hay hijos activos disponibles');
                            setAmount(0);
                        }
                     } else {
                         setAmount(0);
                     }
                }
            } catch (err) {
                console.error('Error al obtener datos:', err);
                setError('Error al obtener información necesaria');
            } finally {
                setIsLoading(false);
            }
        };

        obtenerDatos();
    }, []);
    
    // Escuchar eventos del controlador padre
    useEffect(() => {
        const manejarPlanConfirmado = (event) => {
            const { plan, planActual: planActualEvento } = event.detail;
            
            // Obtener el hijo seleccionado del localStorage si existe
            const hijoSeleccionadoTemp = JSON.parse(localStorage.getItem('hijoSeleccionadoTemp'));
            
            if (hijoSeleccionadoTemp) {
                // Actualizar el monto del hijo con el plan seleccionado
                const hijoActualizado = {
                    ...hijoSeleccionadoTemp,
                    plan_monto: plan.monto
                };
                
                // Agregar el hijo a la selección
                const nuevosSeleccionados = [...hijosSeleccionados, hijoActualizado];
                setHijosSeleccionados(nuevosSeleccionados);
                
                // Recalcular el monto total
                const nuevoMonto = nuevosSeleccionados.reduce((total, h) => {
                    return total + (Number(h.plan_monto) || 0);
                }, 0);
                setAmount(nuevoMonto);
                
                // Limpiar el localStorage temporal
                localStorage.removeItem('hijoSeleccionadoTemp');
                
                // Mostrar mensaje de éxito
                if (plan.id_plan !== planActualEvento?.id_plan) {
                    showSuccessToast(`Plan cambiado a: ${plan.nombre}`);
                } else {
                    showSuccessToast(`Plan renovado: ${plan.nombre}`);
                }
            }
        };
        
        const manejarPlanCancelado = () => {
            // Limpiar el localStorage temporal sin agregar el hijo a la selección
            localStorage.removeItem('hijoSeleccionadoTemp');
        };
        
        // Agregar event listeners
        window.addEventListener('planConfirmado', manejarPlanConfirmado);
        window.addEventListener('planCancelado', manejarPlanCancelado);
        
        // Cleanup
        return () => {
            window.removeEventListener('planConfirmado', manejarPlanConfirmado);
            window.removeEventListener('planCancelado', manejarPlanCancelado);
        };
    }, [hijosSeleccionados]);

    const toggleHijoSeleccionado = async (hijo) => {
        // Prevenir selección si tiene transferencia pendiente o pago por tarjeta pendiente
        if (hijo.transferenciaPendiente || hijo.pagoTarjetaPendiente) {
            return;
        }
        
        const yaSeleccionado = hijosSeleccionados.find(h => h.id_matricula === hijo.id_matricula);
        
        if (yaSeleccionado) {
            // Deseleccionar hijo
            const nuevosSeleccionados = hijosSeleccionados.filter(h => h.id_matricula !== hijo.id_matricula);
            setHijosSeleccionados(nuevosSeleccionados);
            
            // Si no quedan hijos seleccionados, ocultar vista de selección de plan
             if (nuevosSeleccionados.length === 0) {
                 setVistaSeleccionPlan(false);
                 setHijoEnSeleccion(null);
                 setPlanSeleccionado(null);
                 setPlanActual(null);
                 setAmount(0);
             } else {
                 // Recalcular el monto total con los hijos restantes
                 const montoTotal = nuevosSeleccionados.reduce((total, h) => {
                     return total + (Number(h.plan_monto) || 0);
                 }, 0);
                 setAmount(montoTotal);
             }
        } else {
            // Verificar si tiene pagos anteriores
            const resultadoPagos = await verificarPagosAnteriores(hijo.id_matricula);
            
            if (resultadoPagos.tienePagos) {
                // Mostrar vista de selección de plan superpuesta
                setHijoEnSeleccion(hijo);
                setVistaSeleccionPlan(true);
                await cargarPlanesParaHijo(hijo, resultadoPagos.montoActual);
            } else {
                // Si no tiene pagos anteriores, agregar directamente
                const nuevosSeleccionados = [...hijosSeleccionados, hijo];
                setHijosSeleccionados(nuevosSeleccionados);
                const montoTotal = nuevosSeleccionados.reduce((total, h) => {
                    return total + (Number(h.plan_monto) || 0);
                }, 0);
                setAmount(montoTotal);
            }
        }
    };

    const validarArchivo = (file) => {
        setArchivoError('');
        
        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
            setArchivoError('Solo se permiten archivos de imagen');
            showWarningToast('Solo se permiten archivos de imagen');
            return false;
        }

        // Validar tamaño (5MB = 5 * 1024 * 1024 bytes)
        const tamañoMaximo = 5 * 1024 * 1024;
        if (file.size > tamañoMaximo) {
            setArchivoError('El archivo no debe superar los 5MB');
            showWarningToast('El archivo no debe superar los 5MB');
            return false;
        }

        return true;
    };

    const handleComprobanteChange = (e) => {
        const archivo = e.target.files[0];
        if (archivo && validarArchivo(archivo)) {
            setComprobante(archivo);
            setNombreArchivo(archivo.name);
        } else {
            e.target.value = ''; // Limpiar el input
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file && validarArchivo(file)) {
            setComprobante(file);
            setNombreArchivo(file.name);
        }
    };

    const eliminarComprobante = () => {
        setComprobante(null);
        setNombreArchivo('');
    };

    const handleSubmit = async () => {
        if (!comprobante || hijosSeleccionados.length === 0 || !amount) {
            setError('Complete todos los campos requeridos y seleccione al menos un hijo');
            return;
        }
        setIsProcessing(true);
        setError('');

        try {
            const reader = new FileReader();
            
            // Convertir la lectura del archivo en una Promesa
            const readFileAsDataURL = () => {
                return new Promise((resolve, reject) => {
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(comprobante);
                });
            };

            // Esperar a que se lea el archivo
            const base64Data = await readFileAsDataURL();
            
            // Generar un código de transferencia único para todos los pagos múltiples (solo números)
            const codigoTransferencia = Date.now().toString();
            
            // Obtener los planes una vez antes del bucle
            let planesResponse = null;
            try {
                planesResponse = await obtenerPlanes();
            } catch (error) {
                console.error('Error al obtener planes:', error);
                planesResponse = { success: false, data: [] };
            }
            
            for (const hijo of hijosSeleccionados) {
                // Verificar si el hijo tiene un plan diferente al original (plan_monto vs plan original)
                // Si el plan_monto del hijo es diferente al plan original, significa que se seleccionó un nuevo plan
                if (hijo.plan_monto && hijo.id_plan && planesResponse.success) {
                    // Obtener el plan original del hijo para comparar
                    try {
                        const planOriginal = planesResponse.data.find(plan => plan.id_plan === hijo.id_plan);
                        const planSeleccionado = planesResponse.data.find(plan => Number(plan.monto) === Number(hijo.plan_monto));
                        
                        // Si el plan seleccionado es diferente al original, actualizar la matrícula
                        if (planSeleccionado && planOriginal && planSeleccionado.id_plan !== planOriginal.id_plan) {
                            const updateResponse = await actualizarMatricula(hijo.id_matricula, {
                                id_plan: planSeleccionado.id_plan
                            });
                            
                            if (updateResponse.success) {
                                console.log(`Plan actualizado para matrícula ${hijo.id_matricula} de ${planOriginal.nombre} a ${planSeleccionado.nombre}`);
                            } else {
                                console.warn(`No se pudo actualizar el plan para matrícula ${hijo.id_matricula}`);
                            }
                        }
                    } catch (updateError) {
                        console.error('Error al actualizar plan:', updateError);
                    }
                }
                
                // Usar el monto del plan del hijo
                const montoAPagar = hijo.plan_monto || amount;
                
                // Debug: Mostrar datos antes de enviar la petición
                console.log('=== DATOS ENVIADOS AL BACKEND (pago_multiple_transferencia.js) ===');
                console.log('montoAPagar:', montoAPagar);
                console.log('hijo.id_matricula:', hijo.id_matricula);
                console.log('hijo.nombre:', hijo.nombre);
                console.log('mes:', new Date().getMonth() + 1);
                console.log('año:', new Date().getFullYear());
                console.log('codigoTransferencia:', codigoTransferencia);
                console.log('base64Data (primeros 100 chars):', base64Data ? base64Data.substring(0, 100) + '...' : 'null');
                console.log('================================================================');
                
                // Obtener el plan seleccionado para este hijo
                const planSeleccionadoHijo = planesResponse.success ? 
                    planesResponse.data.find(plan => Number(plan.monto) === Number(hijo.plan_monto)) : null;
                
                const response = await pagoTransferencia(
                    montoAPagar,
                    hijo.id_matricula,
                    new Date().getMonth() + 1,
                    new Date().getFullYear(),
                    base64Data,
                    codigoTransferencia,
                    planSeleccionadoHijo?.id_plan // Enviar el id del plan seleccionado para este hijo
                );
                if (!response.success) {
                    throw new Error(`Error en el pago para ${hijo.nombre}`);
                }
            }

            showSuccessToast('Comprobantes enviados exitosamente');
            
            // Limpiar localStorage después del pago exitoso
            localStorage.removeItem('planSeleccionado');
            localStorage.removeItem('hijoSeleccionadoTemp');
            
            // Recargar la página después de 3 segundos
            setTimeout(() => {
                window.location.reload();
            }, 3000);
            
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <Loader className="w-8 h-8 animate-spin text-blue-600 mb-4" />
                <p className="text-gray-600">Cargando información de pagos...</p>
            </div>
        );
    }

    if (amount === 0 && hijosSeleccionados.length > 0) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <p className="text-red-600 font-semibold">No se pudo obtener el valor de la mensualidad. Contacte a soporte.</p>
            </div>
        );
    }



    // Función para manejar selección de plan
    const handleSeleccionarPlan = (plan) => {
        setPlanSeleccionado(plan);
        setAmount(Number(plan.monto));
    };

    // Función para confirmar cambio de plan
    const confirmarCambioPlan = async (e) => {
        // Prevenir comportamiento por defecto para evitar recarga de página
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        
        if (!planSeleccionado) {
            showErrorToast('Por favor selecciona un plan');
            return;
        }

        try {
            // Actualizar plan para todos los hijos seleccionados que tienen pagos anteriores
            for (const hijo of hijosSeleccionados) {
                const tienePagos = await verificarPagosAnteriores(hijo.id_matricula);
                if (tienePagos) {
                    await actualizarMatricula(hijo.id_child, {
                        id_plan: planSeleccionado.id_plan
                    });
                }
            }

            // Guardar el plan seleccionado y el plan actual en localStorage para que los componentes de pago lo usen
            localStorage.setItem('planSeleccionadoMultiple', JSON.stringify(planSeleccionado));
            if (planActual) {
                localStorage.setItem('planActualMultiple', JSON.stringify(planActual));
            }
            
            // Notificar a los componentes de pago que se confirmó el plan
            const evento = new CustomEvent('planConfirmado', {
                detail: {
                    plan: planSeleccionado,
                    planActual: planActual
                }
            });
            window.dispatchEvent(evento);

            showSuccessToast(planActual && planSeleccionado.id_plan !== planActual.id_plan ? 
                'Plan cambiado exitosamente' : 'Plan renovado exitosamente');
            
            // Actualizar el monto con el nuevo plan
            setAmount(Number(planSeleccionado.monto));
            setMostrarSeleccionPlan(false);
        } catch (error) {
            console.error('Error al actualizar plan:', error);
            showErrorToast('Error al actualizar el plan: ' + (error.message || 'Ocurrió un error inesperado'));
        }
    };
    
    // Función para manejar selección de plan en la vista superpuesta
    const handleSeleccionarPlanVista = (e, plan) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        
        setPlanSeleccionado(plan);
        
        // NO actualizar el monto aquí - solo cuando se confirme el plan
        // setAmount(Number(plan.monto) || 0);
        
        // Guardar temporalmente en localStorage
        localStorage.setItem('planSeleccionadoTemp', JSON.stringify(plan));
    };
    
    // Función para confirmar el plan seleccionado en la vista superpuesta
    const confirmarPlanVista = async (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        
        if (!planSeleccionado || !hijoEnSeleccion) {
            showErrorToast('Por favor selecciona un plan');
            return;
        }
        
        try {
            // Actualizar el plan del hijo
            await actualizarMatricula(hijoEnSeleccion.id_child, {
                id_plan: planSeleccionado.id_plan
            });
            
            // Agregar el hijo a la selección con el nuevo plan
            const hijoActualizado = {
                ...hijoEnSeleccion,
                plan_monto: planSeleccionado.monto,
                id_plan: planSeleccionado.id_plan
            };
            
            const nuevosSeleccionados = [...hijosSeleccionados, hijoActualizado];
            setHijosSeleccionados(nuevosSeleccionados);
            
            // Calcular nuevo monto total
            const montoTotal = nuevosSeleccionados.reduce((total, h) => {
                return total + (Number(h.plan_monto) || 0);
            }, 0);
            setAmount(montoTotal);
            
            // Guardar información del plan en localStorage
            localStorage.setItem('planSeleccionadoMultiple', JSON.stringify(planSeleccionado));
            if (planActual) {
                localStorage.setItem('planActualMultiple', JSON.stringify(planActual));
            }
            
            showSuccessToast(planActual && planSeleccionado.id_plan !== planActual.id_plan ? 
                'Plan cambiado exitosamente' : 'Plan renovado exitosamente');
            
            // Cerrar la vista de selección
            setVistaSeleccionPlan(false);
            setHijoEnSeleccion(null);
            setPlanSeleccionado(null);
            setPlanActual(null);
            
        } catch (error) {
            console.error('Error al confirmar plan:', error);
            showErrorToast('Error al confirmar el plan: ' + (error.message || 'Ocurrió un error inesperado'));
        }
    };
    
    // Función para cancelar selección de plan en la vista superpuesta
    const cancelarSeleccionPlanVista = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        
        try {
            // Limpiar estados temporales
            localStorage.removeItem('planSeleccionadoTemp');
            
            // Cerrar la vista de selección
            setVistaSeleccionPlan(false);
            setHijoEnSeleccion(null);
            setPlanSeleccionado(null);
            setPlanActual(null);
            
        } catch (error) {
            console.error('Error al cancelar selección de plan:', error);
            showErrorToast('Error al cancelar: ' + (error.message || 'Ocurrió un error inesperado'));
        }
    };
    
    // Función para cancelar selección de plan (mantener para compatibilidad)
    const cancelarSeleccionPlan = (e) => {
        // Prevenir comportamiento por defecto para evitar recarga de página
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        
        try {
            // Limpiar el localStorage temporal
            localStorage.removeItem('hijoSeleccionadoTemp');
            
            // Notificar a los componentes de pago que se canceló el plan
            const evento = new CustomEvent('planCancelado');
            window.dispatchEvent(evento);
            
            // Restaurar el plan actual si existe
            if (planActual) {
                setPlanSeleccionado(planActual);
                setAmount(Number(planActual.monto));
            }
            
            // Ocultar el modal de selección de plan
            setMostrarSeleccionPlan(false);
        } catch (error) {
            console.error('Error al cancelar selección de plan:', error);
            showErrorToast('Error al cancelar: ' + (error.message || 'Ocurrió un error inesperado'));
        }
    };

    // Función para volver a la selección de planes
    const volverAPlanes = () => {
        setMostrarSeleccionPlan(true);
    };

    // Función para renderizar descripción del plan con viñetas
    const renderDescripcion = (descripcion) => {
        if (!descripcion) return null;
        
        const partes = descripcion.split(';');
        
        if (partes.length <= 1) {
            return <p className="text-sm text-gray-600">{descripcion}</p>;
        }
        
        const descripcionBase = partes[0].trim();
        const beneficios = partes.slice(1).filter(beneficio => beneficio.trim());
        
        return (
            <div>
                {descripcionBase && (
                    <p className="text-sm text-gray-600 mb-3">{descripcionBase}</p>
                )}
                {beneficios.length > 0 && (
                    <div>
                        <p className="font-semibold text-gray-700 mb-2 text-sm">Incluye:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-600 ml-2">
                            {beneficios.map((beneficio, index) => (
                                <li key={index} className="text-sm leading-relaxed">
                                    {beneficio.trim()}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    // Calcular el monto total sumando los montos individuales de cada hijo seleccionado
    const totalAmount = hijosSeleccionados.reduce((total, hijo) => {
        return total + (Number(hijo.plan_monto) || 0);
    }, 0);

    return (
        <div className="container mx-auto p-2 sm:p-6">
            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                className="mt-16"
                style={{ zIndex: 9999 }}
            />
            
            {/* Vista Superpuesta de Selección de Plan */}
            {vistaSeleccionPlan && hijoEnSeleccion && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="text-center mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    Seleccionar Plan para {hijoEnSeleccion.nombre}
                                </h2>
                                <p className="text-gray-600">Elige el plan que deseas renovar o cambiar</p>
                            </div>
                            
                            {loadingPlanes ? (
                                <div className="flex justify-center items-center py-8">
                                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                                    <span className="ml-2 text-gray-600">Cargando planes...</span>
                                </div>
                            ) : (
                                <>
                                    {/* Lista de Planes Disponibles */}
                                    <div className="space-y-4 mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800">Selecciona tu Plan</h3>
                                        {planes.map((plan) => (
                                            <div 
                                                key={plan.id_plan} 
                                                onClick={(e) => handleSeleccionarPlanVista(e, plan)}
                                                className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                                                    planSeleccionado && plan.id_plan === planSeleccionado.id_plan
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-gray-200 hover:border-blue-300'
                                                }`}
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-semibold text-gray-900">
                                                        {plan.nombre}
                                                        {planActual?.id_plan === plan.id_plan && (
                                                            <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                                                Plan Actual
                                                            </span>
                                                        )}
                                                    </h4>
                                                    <span className="text-gray-700 font-bold">${plan.monto}/mes</span>
                                                </div>
                                                {plan.descripcion && (
                                                    <div className="text-gray-600 text-sm mb-2">
                                                        {renderDescripcion(plan.descripcion)}
                                                    </div>
                                                )}
                                                {planSeleccionado && plan.id_plan === planSeleccionado.id_plan && (
                                                    <div className="flex items-center text-blue-600 text-sm font-medium">
                                                        <Check className="w-4 h-4 mr-1" />
                                                        Seleccionado
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Botones de Acción */}
                                    <div className="flex gap-3">
                                        <button
                                            onClick={cancelarSeleccionPlanVista}
                                            className="flex-1 py-3 px-4 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors font-medium"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={confirmarPlanVista}
                                            disabled={!planSeleccionado}
                                            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                                                planSeleccionado
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                        >
                                            {planActual && planSeleccionado && planSeleccionado.id_plan !== planActual.id_plan 
                                                ? 'Cambiar Plan' 
                                                : 'Renovar Plan'
                                            }
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
                {/* Plan Selection UI */}
                {mostrarSeleccionPlan && tienePagosAnteriores && (
                    <>
                        <div className="text-center space-y-2">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Renovar o Cambiar Plan</h2>
                            <p className="text-sm sm:text-base text-gray-500">Selecciona el plan para el pago múltiple por transferencia</p>
                        </div>

                        <div className="backdrop-blur-md bg-white/30 rounded-xl sm:rounded-2xl shadow-xl border border-white/50 p-4 sm:p-8">
                            {loadingPlanes ? (
                                <div className="flex justify-center items-center py-8">
                                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                                    <span className="ml-2 text-gray-600">Cargando planes...</span>
                                </div>
                            ) : (
                                <>
                                    {/* Plan Actual */}
                                    {planActual && (
                                        <div className="mb-6">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Plan Actual</h3>
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-semibold text-blue-900">{planActual.nombre}</h4>
                                                    <span className="text-blue-700 font-bold">${planActual.monto}/mes</span>
                                                </div>
                                                <p className="text-blue-700 text-sm mb-2">{planActual.descripcion}</p>
                                                {planActual.beneficios && (
                                                    <div className="text-blue-600 text-sm">
                                                        <strong>Beneficios:</strong>
                                                        <ul className="list-disc list-inside mt-1">
                                                            {planActual.beneficios.split(';').map((beneficio, index) => (
                                                                <li key={index}>{beneficio.trim()}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Lista de Planes Disponibles */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800">Planes Disponibles</h3>
                                        {planes.map((plan) => (
                                            <div key={plan.id_plan} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-semibold text-gray-900">{plan.nombre}</h4>
                                                    <span className="text-gray-700 font-bold">${plan.monto}/mes</span>
                                                </div>
                                                {plan.descripcion && (
                                                    <div className="text-gray-600 text-sm mb-2">
                                                        {renderDescripcion(plan.descripcion)}
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => handleSeleccionarPlan(plan)}
                                                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                                                        planSeleccionado && plan.id_plan === planSeleccionado.id_plan
                                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {planSeleccionado && plan.id_plan === planSeleccionado.id_plan ? 'Seleccionado' : 'Seleccionar'}
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                                        <button
                                            onClick={cancelarSeleccionPlan}
                                            className="w-full py-3 px-4 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors font-medium"
                                        >
                                            Cancelar
                                        </button>
                                        {planSeleccionado && (
                                            <button
                                                onClick={confirmarCambioPlan}
                                                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium"
                                            >
                                                {planActual && planSeleccionado && planSeleccionado.id_plan !== planActual.id_plan ? 'Cambiar Plan' : 'Renovar Plan'}
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}

                {/* Payment Form */}
                {!mostrarSeleccionPlan && (
                    <>
                        <div className="text-center space-y-2">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Pago múltiple por transferencia</h2>
                            <p className="text-sm sm:text-base text-gray-500">Seleccione los hijos para los que desea realizar el pago</p>
                        
                        </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                    {hijos.map((hijo) => (
                        <div 
                            key={hijo.id_matricula}
                            onClick={() => hijo.requierePago && !hijo.transferenciaPendiente && !hijo.pagoTarjetaPendiente && toggleHijoSeleccionado(hijo)}
                            className={`
                                p-3 sm:p-4 rounded-lg sm:rounded-xl border transition-all duration-200 cursor-pointer
                                ${hijo.requierePago && !hijo.transferenciaPendiente && !hijo.pagoTarjetaPendiente
                                    ? hijosSeleccionados.find(h => h.id_matricula === hijo.id_matricula)
                                        ? 'bg-blue-50 border-blue-200 shadow-sm'
                                        : 'bg-white border-gray-200 hover:border-blue-200'
                                    : 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                                }
                            `}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm sm:text-base font-medium text-gray-900">{hijo.nombre}</h3>
                                    <p className="text-xs sm:text-sm text-gray-500">Matrícula #{hijo.id_matricula}</p>
                                </div>
                                {hijosSeleccionados.find(h => h.id_matricula === hijo.id_matricula) && (
                                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                                )}
                            </div>
                            {!hijo.requierePago && !hijo.pagoTarjetaPendiente && !hijo.transferenciaPendiente && (
                                <p className="text-xs sm:text-sm text-green-600 mt-2">Al día con los pagos</p>
                            )}
                            {hijo.pagoTarjetaPendiente && (
                                <p className="text-xs sm:text-sm text-orange-600 mt-2">Pago por tarjeta pendiente</p>
                            )}
                            {hijo.transferenciaPendiente && (
                                <p className="text-xs sm:text-sm text-yellow-600 mt-2">Transferencia en revisión</p>
                            )}
                            {hijo.requierePago && !hijo.pagoTarjetaPendiente && !hijo.transferenciaPendiente && (
                                <p className="text-xs sm:text-sm text-red-600 mt-2">Requiere pago</p>
                            )}
                        </div>
                    ))}
                </div>

                {hijosSeleccionados.length > 0 && (
                    <div className="space-y-6 sm:space-y-8">
                        <div className="bg-white rounded-lg sm:rounded-2xl shadow-sm p-4 sm:p-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12">
                                <div className="space-y-6 sm:space-y-8">
                                    <div className="bg-gray-50 p-4 sm:p-6 rounded-lg sm:rounded-xl">
                                        <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4 sm:mb-6">Seleccione un Banco</h3>
                                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                            {cuentasBancarias.map((cuenta) => (
                                                <div
                                                    key={cuenta.id_cuenta}
                                                    onClick={() => setCuentaSeleccionada(cuenta)}
                                                    className={`p-3 sm:p-4 rounded-lg cursor-pointer transition-all ${
                                                        cuentaSeleccionada?.id_cuenta === cuenta.id_cuenta
                                                        ? 'bg-blue-100 border-2 border-blue-400'
                                                        : 'bg-white border-2 border-gray-100 hover:border-blue-200'
                                                    }`}
                                                >
                                                    <h4 className="text-xs sm:text-sm font-medium text-gray-800">{cuenta.banco}</h4>
                                                </div>
                                            ))}
                                        </div>

                                        {cuentaSeleccionada && (
                                            <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4 bg-white p-3 sm:p-4 rounded-lg border border-gray-100">
                                                <div className="flex justify-between items-center text-xs sm:text-sm">
                                                    <span className="text-gray-600">tipo de cuenta</span>
                                                    <span className="font-medium text-gray-800">{cuentaSeleccionada.tipo_cuenta}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs sm:text-sm">
                                                    <span className="text-gray-600">número de cuenta</span>
                                                    <span className="font-medium text-gray-800">{cuentaSeleccionada.numero_cuenta}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs sm:text-sm">
                                                    <span className="text-gray-600">beneficiario</span>
                                                    <span className="font-medium text-gray-800">{cuentaSeleccionada.beneficiario}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs sm:text-sm">
                                                    <span className="text-gray-600">Cédula</span>
                                                    <span className="font-medium text-gray-800">{cuentaSeleccionada.ruc}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="bg-blue-50 p-4 sm:p-6 rounded-lg sm:rounded-xl">
                                        <span className="text-xs sm:text-sm text-blue-600">Monto total a transferir</span>
                                        <div className="text-2xl sm:text-4xl font-light text-blue-700 mt-2">
                                            ${totalAmount.toFixed(2)}
                                            <span className="text-xs sm:text-sm text-blue-600 ml-2">
                                                ({hijosSeleccionados.length} {hijosSeleccionados.length === 1 ? 'hijo' : 'hijos'})
                                            </span>
                                        </div>
                                        {/* Mostrar detalle de montos individuales */}
                                        <div className="text-xs text-blue-600 mt-2">
                                            {hijosSeleccionados.map((hijo, index) => (
                                                <span key={hijo.id_matricula}>
                                                    ${Number(hijo.plan_monto || 0).toFixed(2)} x {hijo.nombre}
                                                    {index < hijosSeleccionados.length - 1 ? ' + ' : ''}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4 sm:space-y-6">
                                    <div>
                                        <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4 sm:mb-6">Comprobante de Pago</h3>
                                        
                                        <div className="space-y-4">
                                            {comprobante ? (
                                                <div className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200">
                                                    <div className="flex items-center space-x-3">
                                                        <FaFile className="text-blue-500" size={20} />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-700">{nombreArchivo}</p>
                                                            <button 
                                                                onClick={() => setImagenAmpliada(true)}
                                                                className="text-xs text-blue-600 hover:text-blue-800"
                                                            >
                                                                Ver imagen
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={eliminarComprobante}
                                                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div 
                                                        className={`relative ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
                                                        onDragEnter={handleDragEnter}
                                                        onDragLeave={handleDragLeave}
                                                        onDragOver={handleDragOver}
                                                        onDrop={handleDrop}
                                                    >
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleComprobanteChange}
                                                            className="hidden"
                                                            id="comprobante"
                                                        />
                                                        <label
                                                            htmlFor="comprobante"
                                                            className="flex flex-col items-center justify-center space-y-2 w-full px-3 sm:px-4 py-4 sm:py-6 border-2 border-dashed rounded-lg sm:rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                                                        >
                                                            <FaExchangeAlt className="text-gray-500" size={20} />
                                                            <div className="text-center">
                                                                <p className="text-xs sm:text-sm text-gray-600">Arrastra y suelta la imagen aquí o</p>
                                                                <p className="text-xs sm:text-sm text-blue-600 font-medium">Haz clic para seleccionar</p>
                                                                <p className="text-xs text-gray-500 mt-1">Solo imágenes (máx. 5MB)</p>
                                                            </div>
                                                        </label>
                                                    </div>
                                                    {archivoError && (
                                                        <p className="text-red-500 text-xs sm:text-sm mt-2">{archivoError}</p>
                                                    )}
                                                </div>
                                            )}

                                            {imagenAmpliada && comprobante && (
                                                <div 
                                                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                                                    onClick={() => setImagenAmpliada(false)}
                                                >
                                                    <img 
                                                        src={URL.createObjectURL(comprobante)}
                                                        alt="Comprobante ampliado"
                                                        className="max-w-full max-h-[90vh] object-contain rounded-lg"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {error && (
                                            <div className="mt-4 text-rose-500 text-xs sm:text-sm bg-rose-50 p-3 rounded-lg">{error}</div>
                                        )}

                                        <button
                                            onClick={handleSubmit}
                                            disabled={!comprobante || isProcessing || hijosSeleccionados.length === 0 || archivoError}
                                            className={`
                                                w-full py-3 sm:py-4 mt-4 sm:mt-6 rounded-lg sm:rounded-xl text-white text-sm font-medium transition-all
                                                ${comprobante && !isProcessing && hijosSeleccionados.length > 0 && !archivoError
                                                    ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-xl' 
                                                    : 'bg-gray-200 cursor-not-allowed'
                                                }
                                            `}
                                        >
                                            {isProcessing ? 'Procesando...' : `Confirmar pago de $${totalAmount.toFixed(2)}`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PagoMultipleTransferencia;
